import React, { useEffect, useState } from "react";
import { Modal, ModalBody, Table, Spinner, Alert, Collapse } from "reactstrap";
import contratoService from "services/ContratoService";

// Helper to translate field names to user friendly Portuguese labels
const TradutorCampos = {
  id: "ID",
  numero: "Número",
  objeto: "Objeto",
  inicioVigencia: "Início da Vigência",
  fimVigencia: "Fim da Vigência",
  validade: "Validade",
  tipoContrato: "Tipo de Contrato",
  tipoValidade: "Tipo de Validade",
  validadeMeses: "Validade (Meses)",
  codigo: "Código",
  descricao: "Descrição",
  unidade: "Unidade",
  quantidadeContratada: "Quantidade Contratada",
  precoUnitario: "Preço Unitário",
  layoutRelatorio: "Layout do Relatório",
  idStatus: "Status",
  idLote: "Lote",
  idGestor: "Gestor",
  idEmpresa: "Empresa",
  idConsorcio: "Consórcio",
  idContrato: "Contrato",
  idContratoGrupoItem: "Grupo do Item",
  idContratoItem: "Item do Contrato",
  idAditivo: "Aditivo",
  ativo: "Ativo",
};

function traduzirCampo(campo) {
  return TradutorCampos[campo] || campo.charAt(0).toUpperCase() + campo.slice(1).replace(/([A-Z])/g, " $1");
}

// Helper to format values elegantly
function formatarValor(campo, valor) {
  if (valor === null || valor === undefined) return <em className="text-muted">nulo</em>;
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";

  // Check if it's a date field
  if (["inicioVigencia", "fimVigencia", "data", "dataCriacao"].includes(campo) && typeof valor === "string") {
    try {
      const date = new Date(valor);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("pt-BR");
      }
    } catch { }
  }

  // Check if it's a decimal number field
  if (["precoUnitario", "quantidadeContratada"].includes(campo) && typeof valor === "number") {
    return valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  return String(valor);
}

export default function ModalHistoricoLogs({ isOpen, toggle, tipo, idRegistro, titulo = "Histórico de Alterações" }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (isOpen && idRegistro && tipo) {
      carregarHistorico();
    } else {
      setLogs([]);
      setError(null);
      setOpenId(null);
    }
  }, [isOpen, idRegistro, tipo]);

  const carregarHistorico = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contratoService.getLogs(tipo, idRegistro);
      if (response && response.data) {
        const sorted = [...response.data].sort((a, b) => new Date(b.data) - new Date(a.data));
        setLogs(sorted);
        setOpenId(sorted.length > 0 ? sorted[0].id : null);
      } else {
        setLogs([]);
        setOpenId(null);
      }
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setError("Não foi possível carregar o histórico de alterações.");
    } finally {
      setLoading(false);
    }
  };

  const getAcaoBadge = (acao) => {
    switch (acao) {
      case "CREATE":
        return <span className="badge badge-success px-2 py-1">CRIAÇÃO</span>;
      case "UPDATE":
        return <span className="badge badge-warning text-dark px-2 py-1">ATUALIZAÇÃO</span>;
      case "PUBLISH":
        return <span className="badge badge-primary px-2 py-1">PUBLICAÇÃO</span>;
      case "DELETE":
        return <span className="badge badge-danger px-2 py-1">EXCLUSÃO</span>;
      case "CANCELED":
        return <span className="badge badge-danger px-2 py-1">CANCELAMENTO</span>;
      default:
        return <span className="badge badge-secondary px-2 py-1">{acao}</span>;
    }
  };

  const formatarData = (dataStr) => {
    try {
      const d = new Date(dataStr);
      return d.toLocaleString("pt-BR");
    } catch {
      return dataStr;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="modal-dialog-centered">
      <div className="modal-header d-flex align-items-center justify-content-between border-bottom-0 pb-0">
        <h4 className="modal-title font-weight-bold text-primary">
          <i className="bi bi-clock-history mr-2"></i>
          {titulo}
        </h4>
        <button aria-label="Close" className="close font-weight-light" onClick={toggle} type="button" style={{ outline: 'none' }}>
          <span aria-hidden={true} style={{ fontSize: '1.8rem' }}>×</span>
        </button>
      </div>

      <ModalBody className="px-4 py-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading && (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner color="primary" className="mb-3" />
            <span className="text-muted font-weight-medium">Carregando histórico...</span>
          </div>
        )}

        {error && (
          <Alert color="danger" className="text-center py-3">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </Alert>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-folder-x d-block mb-3" style={{ fontSize: "3rem" }}></i>
            <p className="lead font-weight-medium">Nenhum histórico encontrado</p>
            <p className="small">Este registro ainda não possui nenhuma alteração cadastrada.</p>
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <div className="accordion">
            {logs.map((log) => {
              const isOpen = openId === log.id;
              const diffKeysRaw = log.valorNovo ? Object.keys(log.valorNovo) : [];
              const diffKeys = diffKeysRaw.filter(k => !k.endsWith('Descricao'));

              return (
                <div key={log.id} className="card shadow-sm mb-2 border">
                  {/* Accordion header — clicável */}
                  <div
                    className="card-header py-2 px-3"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setOpenId(isOpen ? null : log.id)}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="font-weight-bold text-dark mr-2" style={{ fontSize: "0.9rem" }}>
                          {log.usuario}
                        </span>
                        {getAcaoBadge(log.acao)}
                        <small className="text-muted ml-2">
                          <i className="bi bi-calendar3 mr-1"></i>
                          {formatarData(log.data)}
                        </small>
                      </div>
                      <i className={`bi bi-chevron-${isOpen ? "up" : "down"} text-muted`}></i>
                    </div>
                  </div>

                  {/* Accordion body */}
                  <Collapse isOpen={isOpen}>
                    <div className="card-body py-2 px-3">
                      {/* Observation note */}
                      {log.observacao && (() => {
                        try {
                          const parsed = JSON.parse(log.observacao);
                          if (typeof parsed === 'object' && parsed !== null) return null;
                        } catch (e) {}
                        return (
                          <p className="text-muted small mb-2">
                            <strong>Motivo/Nota:</strong> "{log.observacao}"
                          </p>
                        );
                      })()}

                      {/* Difference data table */}
                      {diffKeys.length > 0 ? (
                        <div className="rounded overflow-hidden">
                          <Table size="sm" className="mb-0 text-left" style={{ fontSize: "0.85rem", tableLayout: "fixed", width: "100%" }}>
                            <thead className="bg-light text-secondary">
                              <tr>
                                <th className="font-weight-bold" style={{ width: "25%" }}>Campo</th>
                                <th className="font-weight-bold" style={{ width: "37.5%" }}>Valor Anterior</th>
                                <th className="font-weight-bold" style={{ width: "37.5%" }}>Valor Novo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diffKeys.map((key) => {
                                const valAnt = log.valorAnterior ? log.valorAnterior[key] : null;
                                const valNov = log.valorNovo ? log.valorNovo[key] : null;
                                const descAnt = log.valorAnterior ? log.valorAnterior[key + 'Descricao'] : null;
                                const descNov = log.valorNovo ? log.valorNovo[key + 'Descricao'] : null;

                                const renderCell = (val, desc) => {
                                  if (val === null || val === undefined) return <em className="text-muted">nulo</em>;
                                  const formatted = formatarValor(key, val);
                                  if (desc) {
                                    return <>{formatted} <span className="text-muted font-weight-normal">({desc})</span></>;
                                  }
                                  return formatted;
                                };

                                return (
                                  <tr key={key}>
                                    <td className="font-weight-medium text-dark" style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{traduzirCampo(key)}</td>
                                    <td style={{ backgroundColor: "#fff5f5", color: "#c0392b", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                      {renderCell(valAnt, descAnt)}
                                    </td>
                                    <td style={{ backgroundColor: "#f0fbf0", color: "#27ae60", wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                                      {renderCell(valNov, descNov)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        log.acao !== "DELETE" && (
                          <div className="text-muted small pl-2 border-left">
                            Nenhum campo auditável foi modificado.
                          </div>
                        )
                      )}

                      {log.acao === "DELETE" && (
                        <div className="alert alert-danger py-1 px-2 small d-inline-block mb-0 mt-2">
                          <i className="bi bi-trash mr-1"></i> Registro excluído / desativado.
                        </div>
                      )}
                      {log.acao === "CANCELED" && (
                        <div className="alert alert-danger py-1 px-2 small d-inline-block mb-0 mt-2">
                          <i className="bi bi-x-circle mr-1"></i> Contrato cancelado.
                        </div>
                      )}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
